<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper'

const props = defineProps({
  activity: { type: Object, required: true },
})
const emit = defineEmits(['edit', 'complete', 'delete'])

const { t, locale } = useI18n()
const route = useRoute()

// conversation_id ya viene como display_id desde el backend (ver _activity.json.jbuilder)
const conversationRoute = computed(() => {
  if (!props.activity.conversation_id) return null
  return frontendURL(
    conversationUrl({
      accountId: route.params.accountId,
      id: props.activity.conversation_id,
    })
  )
})

const typeIcon = { task: 'clipboard', call: 'call', meeting: 'calendar', email: 'mail', note: 'document' }

const priorityConfig = computed(() => ({
  high:   { label: t('TASKS.FORM.PRIORITY_OPTIONS.high'),   cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
  medium: { label: t('TASKS.FORM.PRIORITY_OPTIONS.medium'), cls: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' },
  low:    { label: t('TASKS.FORM.PRIORITY_OPTIONS.low'),    cls: 'bg-n-slate-9/10 text-n-slate-11' },
}))

const statusConfig = computed(() => ({
  pending:     { label: t('TASKS.FORM.STATUS_OPTIONS.pending'),     cls: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' },
  in_progress: { label: t('TASKS.FORM.STATUS_OPTIONS.in_progress'), cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  done:        { label: t('TASKS.FORM.STATUS_OPTIONS.done'),        cls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  cancelled:   { label: t('TASKS.FORM.STATUS_OPTIONS.cancelled'),   cls: 'bg-n-slate-9/10 text-n-slate-11' },
}))

const isOverdue = computed(() => {
  if (props.activity.status === 'done' || props.activity.status === 'cancelled') return false
  return props.activity.due_at && new Date(props.activity.due_at) < new Date()
})

const isDone = computed(() => props.activity.status === 'done')

const formattedDueAt = computed(() => {
  if (!props.activity.due_at) return ''
  return new Intl.DateTimeFormat(locale.value, {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  }).format(new Date(props.activity.due_at))
})
</script>

<template>
  <div
    class="rounded-lg border p-3 transition-all"
    :class="[
      isDone ? 'opacity-60 border-n-weak bg-n-slate-9/5' : 'border-n-weak bg-white dark:bg-n-solid-2',
      isOverdue && !isDone ? 'border-red-300 dark:border-red-800' : '',
    ]"
  >
    <div class="flex items-start justify-between gap-2">
      <div class="flex items-center gap-2 min-w-0">
        <fluent-icon
          :icon="typeIcon[activity.activity_type] || 'clipboard'"
          size="14"
          class="text-n-slate-11 shrink-0"
        />
        <span
          class="text-sm font-medium text-n-slate-12 truncate"
          :class="{ 'line-through text-n-slate-11': isDone }"
        >
          {{ activity.subject }}
        </span>
      </div>
      <div class="flex items-center gap-1 shrink-0">
        <router-link
          v-if="conversationRoute"
          :to="conversationRoute"
          class="inline-flex items-center gap-1 px-3 py-1 bg-n-brand/10 hover:bg-n-brand/20 text-n-brand rounded-full text-xs font-medium transition-colors no-underline whitespace-nowrap"
        >
          {{ $t('TASKS.CARD.VIEW_CONVERSATION') }}
        </router-link>
        <button
          v-if="!isDone"
          :title="$t('TASKS.CARD.COMPLETE_TITLE')"
          class="p-1 text-n-slate-11 hover:text-green-600 transition-colors rounded"
          @click="emit('complete', activity)"
        >
          <fluent-icon icon="checkmark-circle" size="14" />
        </button>
        <button
          :title="$t('TASKS.CARD.EDIT_TITLE')"
          class="p-1 text-n-slate-11 hover:text-n-brand transition-colors rounded"
          @click="emit('edit', activity)"
        >
          <fluent-icon icon="edit" size="14" />
        </button>
        <button
          :title="$t('TASKS.CARD.DELETE_TITLE')"
          class="p-1 text-n-slate-11 hover:text-red-500 transition-colors rounded"
          @click="emit('delete', activity)"
        >
          <fluent-icon icon="delete" size="14" />
        </button>
      </div>
    </div>

    <div class="flex items-center gap-2 mt-2 flex-wrap">
      <span
        class="text-xs px-2 py-0.5 rounded-full font-medium"
        :class="priorityConfig[activity.priority]?.cls"
      >
        {{ priorityConfig[activity.priority]?.label }}
      </span>
      <span
        class="text-xs px-2 py-0.5 rounded-full font-medium"
        :class="statusConfig[activity.status]?.cls"
      >
        {{ statusConfig[activity.status]?.label }}
      </span>
      <span
        v-if="formattedDueAt"
        class="text-xs ml-auto"
        :class="isOverdue ? 'text-red-500 font-medium' : 'text-n-slate-11'"
      >
        {{ formattedDueAt }}
      </span>
    </div>

    <p v-if="activity.description" class="text-xs text-n-slate-11 mt-2 line-clamp-2">
      {{ activity.description }}
    </p>
  </div>
</template>
